/*
  Comments on a puzzle: the shapes that cross the wire, and the rules both clients and the server
  apply to what a player types. The server is the authority on every rule here; the clients use the
  same functions so they can say what's wrong before sending.
*/

import type { GameType, SimpleDate } from './types';

/** The reactions on offer. The server stores any emoji as text, so widening this is all it takes. */
export const REACTION_EMOJIS = ['👍', '❤️', '😂', '🎉', '🤔', '😢'] as const;

export const isReactionEmoji = (emoji: unknown): emoji is (typeof REACTION_EMOJIS)[number] =>
  (REACTION_EMOJIS as readonly unknown[]).includes(emoji);

// Two, not three, so that a name in a script whose names are short (太郎) fits.
export const SCREEN_NAME_MIN_LENGTH = 2;
export const SCREEN_NAME_MAX_LENGTH = 20;
export const COMMENT_BODY_MAX_LENGTH = 2000;

// Words of letters and digits from any script, _ and -, separated by single spaces. Marks are the
// accents and vowel signs that some scripts write as separate characters.
const SCREEN_NAME_RE = /^[\p{L}\p{M}\p{N}_-]+( [\p{L}\p{M}\p{N}_-]+)*$/u;

/**
 * A screen name as it will be stored: trimmed, with runs of whitespace collapsed to one space, and
 * composed (NFC) so the two ways of typing "é" are one name rather than two that look alike.
 */
export const normalizeScreenName = (raw: string): string => raw.normalize('NFC').trim().replace(/\s+/g, ' ');

/** What's wrong with a (normalized) screen name, in words for the player, or null if it's fine. */
export function screenNameError(name: string): string | null {
  // Counted in characters as the database counts them, not in UTF-16 units.
  const length = [...name].length;
  if (length < SCREEN_NAME_MIN_LENGTH) return `Use at least ${SCREEN_NAME_MIN_LENGTH} characters`;
  if (length > SCREEN_NAME_MAX_LENGTH) return `Use at most ${SCREEN_NAME_MAX_LENGTH} characters`;
  if (!SCREEN_NAME_RE.test(name)) return 'Use only letters, numbers, spaces, _ and -';
  return null;
}

/** A comment as it will be stored. Inner whitespace and line breaks are the player's to keep. */
export const normalizeCommentBody = (raw: string): string => raw.trim();

/** What's wrong with a (normalized) comment, in words for the player, or null if it's fine. */
export function commentBodyError(body: string): string | null {
  if (!body) return 'Write something first';
  if (body.length > COMMENT_BODY_MAX_LENGTH) return `Keep it under ${COMMENT_BODY_MAX_LENGTH} characters`;
  return null;
}

/**
 * The author's board as it stood when they posted, for "I'm stuck here, can anyone help?" Only the
 * comment that starts a thread carries one.
 *
 * It carries the play, not the puzzle: a comment belongs to a board, so whoever is reading already
 * has the puzzle to draw the play on. `playData` is the game's own play blob less its statistics,
 * which is `cells` plus whatever else that game draws from.
 */
export interface BoardAttachment {
  type: 'board';
  /** Whether the puzzle was solved, which some boards draw differently. */
  complete: boolean;
  playData: { cells: unknown[][]; [extra: string]: unknown };
}

/**
 * One line drawn over a board: a pen colour, by its place in the palette, and the points it runs
 * through. A point is [x, y] in thousandths of the drawn board's width and height, so a drawing
 * lands in the same place at whatever size the board is shown.
 */
export interface DrawingStroke {
  color: number;
  points: [number, number][];
}

/**
 * A reply's answer drawn over the board its thread began with. It carries the lines and not the
 * board: the thread's first comment has that, and a reply whose thread has lost it has nothing to
 * be drawn on.
 */
export interface DrawingAttachment {
  type: 'drawing';
  strokes: DrawingStroke[];
}

/**
 * The author's result on the puzzle, as "Include results" shares it once they've solved it: their time
 * and how many hints and checks they used. Only the comment that starts a thread carries one.
 */
export interface ResultsAttachment {
  type: 'results';
  durationSec: number;
  assists: number;
}

export type CommentAttachment = BoardAttachment | DrawingAttachment | ResultsAttachment;

/** How many pens there are to draw with. What colour each is belongs to whoever draws them. */
export const DRAWING_COLOR_COUNT = 2;
/** The span of a drawing's coordinates along each side of the board. */
export const DRAWING_EXTENT = 1000;
const DRAWING_MAX_STROKES = 100;
const DRAWING_MAX_POINTS = 200;

/** Generous for the largest board there is, and small enough that a page of them is still a small page. */
export const COMMENT_ATTACHMENT_MAX_LENGTH = 32_000;
const ATTACHMENT_MAX_SIDE = 64;

function parseBoardAttachment(raw: Partial<BoardAttachment>): BoardAttachment | null {
  const { complete, playData } = raw;
  if (typeof complete !== 'boolean' || !playData || typeof playData !== 'object') return null;

  const { cells } = playData;
  const isRow = (row: unknown) => Array.isArray(row) && row.length <= ATTACHMENT_MAX_SIDE;
  if (!Array.isArray(cells) || !cells.length || cells.length > ATTACHMENT_MAX_SIDE || !cells.every(isRow)) return null;

  return { type: 'board', complete, playData };
}

function parseDrawingAttachment(raw: Partial<DrawingAttachment>): DrawingAttachment | null {
  const { strokes } = raw;
  if (!Array.isArray(strokes) || !strokes.length || strokes.length > DRAWING_MAX_STROKES) return null;

  const isCoordinate = (n: unknown) => Number.isInteger(n) && (n as number) >= 0 && (n as number) <= DRAWING_EXTENT;
  const isPoint = (point: unknown) => Array.isArray(point) && point.length === 2 && point.every(isCoordinate);
  const parsed: DrawingStroke[] = [];
  for (const stroke of strokes as Partial<DrawingStroke>[]) {
    if (!stroke || typeof stroke !== 'object') return null;
    const { color, points } = stroke;
    if (!Number.isInteger(color) || color! < 0 || color! >= DRAWING_COLOR_COUNT) return null;
    if (!Array.isArray(points) || points.length < 2 || points.length > DRAWING_MAX_POINTS) return null;
    if (!points.every(isPoint)) return null;
    parsed.push({ color: color!, points });
  }
  return { type: 'drawing', strokes: parsed };
}

/** Longer than any solve worth sharing, and short enough to keep the clock readable. */
const RESULTS_MAX_DURATION_SEC = 100 * 3600;
const RESULTS_MAX_ASSISTS = 10_000;

function parseResultsAttachment(raw: Partial<ResultsAttachment>): ResultsAttachment | null {
  const { durationSec, assists } = raw;
  const isCount = (n: unknown, max: number): n is number =>
    Number.isInteger(n) && (n as number) >= 0 && (n as number) <= max;
  if (!isCount(durationSec, RESULTS_MAX_DURATION_SEC) || !isCount(assists, RESULTS_MAX_ASSISTS)) return null;
  return { type: 'results', durationSec, assists };
}

/**
 * An attachment as it will be stored, or null if `raw` isn't one. This checks the envelope: a
 * board's worth of rows of a sane size, or lines that stay on the board. Whether a board's cells
 * fit the puzzle is for the game that draws them to say, which is why a client must be ready for
 * one that doesn't.
 */
export function parseCommentAttachment(raw: unknown): CommentAttachment | null {
  if (!raw || typeof raw !== 'object') return null;
  const { type } = raw as { type?: unknown };
  const attachment =
    type === 'board'
      ? parseBoardAttachment(raw as Partial<BoardAttachment>)
      : type === 'drawing'
        ? parseDrawingAttachment(raw as Partial<DrawingAttachment>)
        : type === 'results'
          ? parseResultsAttachment(raw as Partial<ResultsAttachment>)
          : null;
  return attachment && JSON.stringify(attachment).length <= COMMENT_ATTACHMENT_MAX_LENGTH ? attachment : null;
}

/** How far a point may sit from the line through its neighbours and still be dropped, in drawing units. */
const STROKE_TOLERANCE = 6;

const distanceToSegment = ([x, y]: [number, number], [x1, y1]: [number, number], [x2, y2]: [number, number]) => {
  const [dx, dy] = [x2 - x1, y2 - y1];
  const length = dx * dx + dy * dy;
  const t = length ? Math.max(0, Math.min(1, ((x - x1) * dx + (y - y1) * dy) / length)) : 0;
  return Math.hypot(x - (x1 + t * dx), y - (y1 + t * dy));
};

/**
 * Turns the trail a pointer left into the few line segments that say the same thing: points are
 * snapped to whole drawing units and kept on the board, and any that sit close to the line through
 * their neighbours are dropped (Ramer-Douglas-Peucker). A wobbly hand-drawn line comes out as a
 * handful of points rather than hundreds, which is what makes a drawing small enough to store.
 *
 * A tap, which leaves a single point, comes out as a segment of no length: drawn with round ends,
 * that is a dot.
 */
export function simplifyStroke(trail: [number, number][]): [number, number][] {
  const snap = (n: number) => Math.max(0, Math.min(DRAWING_EXTENT, Math.round(n)));
  const points = trail.map(([x, y]): [number, number] => [snap(x), snap(y)]);
  if (points.length === 0) return [];
  if (points.length === 1) return [points[0], points[0]];

  const keep = (from: number, to: number): [number, number][] => {
    let [farthest, distance] = [-1, STROKE_TOLERANCE];
    for (let i = from + 1; i < to; i++) {
      const d = distanceToSegment(points[i], points[from], points[to]);
      if (d > distance) [farthest, distance] = [i, d];
    }
    if (farthest < 0) return [points[from]];
    return [...keep(from, farthest), ...keep(farthest, to)];
  };

  const simplified = [...keep(0, points.length - 1), points[points.length - 1]];
  // A stroke too long to store keeps its shape and loses detail: every nth point, and the last.
  if (simplified.length <= DRAWING_MAX_POINTS) return simplified;
  const step = Math.ceil(simplified.length / (DRAWING_MAX_POINTS - 1));
  return [...simplified.filter((_, i) => i % step === 0).slice(0, DRAWING_MAX_POINTS - 1), simplified.at(-1)!];
}

export interface CommentReactionSummary {
  emoji: string;
  count: number;
  /** Whether the viewer is one of the people who reacted with it. */
  mine: boolean;
}

export interface PuzzleComment {
  id: number;
  /** Null for a top-level comment. A reply's parent is always top-level. */
  parentId: number | null;
  /** Null on a deleted comment, along with an empty body. */
  screenName: string | null;
  body: string;
  /** ISO 8601. */
  createdAt: string;
  /** A deleted top-level comment that is kept as a placeholder for its replies. */
  deleted: boolean;
  /** Whether the viewer wrote it. */
  mine: boolean;
  /** Whether the viewer has reported it. */
  reported: boolean;
  /** Null on most comments, and always on a deleted one. */
  attachment: CommentAttachment | null;
  reactions: CommentReactionSummary[];
}

export interface CommentThread extends PuzzleComment {
  replies: PuzzleComment[];
}

/**
 * Arranges a board's comments for display: threads in the order they were started, and each
 * thread's replies in the order they were written, so the whole page reads downward as a
 * conversation and a newcomer passes what has been said on the way to adding to it.
 */
export function buildThreads(comments: PuzzleComment[]): CommentThread[] {
  const oldestFirst = [...comments].sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id - b.id);

  const threads = new Map<number, CommentThread>();
  for (const comment of oldestFirst) {
    if (comment.parentId === null) threads.set(comment.id, { ...comment, replies: [] });
  }
  for (const comment of oldestFirst) {
    if (comment.parentId !== null) threads.get(comment.parentId)?.replies.push(comment);
  }

  return [...threads.values()];
}

/**
 * A reply in a conversation the player has joined in the last few days, written since they last
 * posted there. Everything a notification needs, including where to go to see it.
 */
export interface CommentReply {
  id: number;
  /** The top-level comment the reply sits under. */
  threadId: number;
  game: GameType;
  date: SimpleDate;
  screenName: string | null;
  body: string;
  /** ISO 8601. */
  createdAt: string;
  /** Newer than what the player has read of its puzzle's comments. */
  unread: boolean;
}

/**
 * That the player has read a puzzle's comments up to `seenThrough`: the `createdAt` of the newest reply
 * they were shown there. Naming it, rather than "now", leaves a reply that lands while they read unread.
 */
export interface RepliesSeen {
  game: GameType;
  date: SimpleDate;
  seenThrough: string;
}

/** A puzzle with unread replies, as a list of them shows it. */
export interface UnreadPuzzle {
  game: GameType;
  date: SimpleDate;
  /** How many of its replies are unread. */
  count: number;
}

/** The puzzles among `replies` with something unread, the one with the newest unread reply first. */
export function unreadPuzzles(replies: CommentReply[]): UnreadPuzzle[] {
  const puzzles = new Map<string, UnreadPuzzle>();
  for (const { game, date, unread } of [...replies].sort((a, b) => b.createdAt.localeCompare(a.createdAt))) {
    if (!unread) continue;
    const key = `${game}/${date}`;
    const puzzle = puzzles.get(key) ?? { game, date, count: 0 };
    puzzle.count++;
    puzzles.set(key, puzzle);
  }
  return [...puzzles.values()];
}

/**
 * How far a player has read one puzzle's replies, given the ones that were unread and which of those
 * they've seen: through the newest with nothing unseen older than it, or null if that's none of them.
 *
 * Reading down the page doesn't read replies in the order they were written, since each thread has its
 * own. A reply seen while an older one is still unseen stays unread, rather than marking the older one
 * read along with it.
 */
export function readThrough(unread: CommentReply[], seen: ReadonlySet<number>): string | null {
  let through: string | null = null;
  for (const reply of [...unread].sort((a, b) => a.createdAt.localeCompare(b.createdAt))) {
    if (!seen.has(reply.id)) break;
    through = reply.createdAt;
  }
  return through;
}

/** `replies` as they read once `seen` is recorded: what the server will answer next, shown at once. */
export function withRepliesSeen(replies: CommentReply[], seen: RepliesSeen[]): CommentReply[] {
  return replies.map((reply) =>
    reply.unread && seen.some((s) => s.game === reply.game && s.date === reply.date && reply.createdAt <= s.seenThrough)
      ? { ...reply, unread: false }
      : reply
  );
}

/** What a client remembers of a comment it has just used to block the author. */
export interface BlockNotice {
  /** The comment the block was made from. */
  commentId: number;
  parentId: number | null;
  createdAt: string;
  /** When its thread began: the comment's own time if it is top-level. */
  threadCreatedAt: string;
  screenName: string;
}

/**
 * Puts a stand-in where each just-blocked comment was, so the confirmation appears in the spot the
 * player was looking at rather than somewhere they may have scrolled away from.
 *
 * The server stops sending a blocked player's comments, and the replies under any thread they began.
 * So a block made from a reply leaves a stand-in among that thread's replies when the thread
 * survives, and in the thread's place when it doesn't. `blockedNames` maps each stand-in's id to
 * the name to show there.
 */
export function withBlockNotices(
  comments: PuzzleComment[],
  notices: BlockNotice[]
): { comments: PuzzleComment[]; blockedNames: Map<number, string> } {
  const present = new Set(comments.map((c) => c.id));
  const blockedNames = new Map<number, string>();
  const standIns: PuzzleComment[] = [];

  for (const notice of notices) {
    const threadSurvives = notice.parentId !== null && present.has(notice.parentId);
    const inPlace = notice.parentId === null || threadSurvives;
    const id = inPlace ? notice.commentId : notice.parentId!;
    // Two blocks made in one thread that has since gone share its one stand-in.
    if (blockedNames.has(id)) continue;

    blockedNames.set(id, notice.screenName);
    standIns.push({
      id,
      parentId: threadSurvives ? notice.parentId : null,
      screenName: null,
      body: '',
      createdAt: inPlace ? notice.createdAt : notice.threadCreatedAt,
      deleted: false,
      mine: false,
      reported: false,
      attachment: null,
      reactions: [],
    });
  }

  return { comments: [...comments.filter((c) => !blockedNames.has(c.id)), ...standIns], blockedNames };
}
